/** @file Incrementally recompiles after a document change, reusing cached per-entity results. */
import type { AtlasJson, Doc, DocIndex, Pose2DJson, Warning } from '@pose2d/format/types';
import { DIRECT_STAGES } from '../compile/directStages';
import { runCompile } from '../compile/runCompile';
import type { TextureLayout } from '../textureLayout';
import { cachedStages } from './cachedStages';
import type { CompileCache } from './compileCache';
import { sameLayout } from './sameLayout';

/**
 * Same result as compileDoc, reusing cached per-entity results; mutates only the cache. Caller contract: `doc` is
 * immutable (immer-produced; an in-place edit is not seen), `index` is `docIndex(doc)` of that same document (the
 * cache never checks it), a packed layout's atlas is never edited in place, and the result is never mutated (it
 * shares objects with earlier results and the cache, W2-20).
 */
export function recompile(cache: CompileCache, doc: Doc, index: DocIndex, layout: TextureLayout): { json: Pose2DJson; atlas: AtlasJson; warnings: Warning[] } {
  const last = cache.lastResult;
  if (last && cache.lastDoc === doc && cache.lastLayout && sameLayout(cache.lastLayout, layout)) return last;
  const result = runCompile(doc, index, layout, cachedStages(cache, DIRECT_STAGES));
  cache.lastDoc = doc;
  cache.lastLayout = layout;
  cache.lastResult = result;
  return result;
}
